import type { ManualIncentiveEditEntry } from '@makemydeal/dr-dash-store/types/manualIncentiveTypes';
import { toShownManualRow } from '@makemydeal/dr-dash-store/selectors/incentivesSelectors';
import { generateEditId } from './rebatesUtils';

const hasCode = (inc: ManualIncentiveEditEntry): boolean => inc.code != null && String(inc.code) !== '';

const isSameIncentiveIdentity = (a: ManualIncentiveEditEntry, b: ManualIncentiveEditEntry): boolean => {
    if (hasCode(a) || hasCode(b)) {
        return String(a.code) === String(b.code);
    }
    return a.name === b.name && a.type === b.type;
};

const hasSameProvenance = (a: ManualIncentiveEditEntry, b: ManualIncentiveEditEntry): boolean =>
    Boolean(a.manual) === Boolean(b.manual);

const isSameRowAcrossSync = (
    existingIncentive: ManualIncentiveEditEntry,
    incomingIncentive: ManualIncentiveEditEntry,
    requireSameProvenance: boolean
): boolean =>
    isSameIncentiveIdentity(existingIncentive, incomingIncentive) &&
    (!requireSameProvenance || hasSameProvenance(existingIncentive, incomingIncentive));

/**
 * Merges a previous incentive with updated data from Redux,
 * preserving edit ID and resetting deleted flag
 */
const mergeWithUpdatedIncentive = (
    existingIncentive: ManualIncentiveEditEntry,
    incomingIncentive: ManualIncentiveEditEntry
): ManualIncentiveEditEntry => {
    return { ...incomingIncentive, _editId: existingIncentive._editId, deleted: false };
};

/**
 * Determines if an incentive should be preserved (deleted or newly added)
 */
const shouldPreserveIncentive = (incentive: ManualIncentiveEditEntry): boolean | undefined => {
    return incentive.deleted || incentive.isNewIncentive;
};

const carriesEditId = (incentive: ManualIncentiveEditEntry): boolean => Boolean(incentive._editId);

const isEditIdAuthoritative = (
    existingIncentive: ManualIncentiveEditEntry,
    incomingIncentives: ManualIncentiveEditEntry[],
    requireSameProvenance: boolean
): boolean =>
    requireSameProvenance &&
    Boolean(existingIncentive.manual) &&
    carriesEditId(existingIncentive) &&
    incomingIncentives.some(carriesEditId);

/**
 * Finds the matching incoming incentive for an existing one.
 * Matches by _editId first (stable row identity), then falls back to code identity.
 */
const findMatchingIncoming = (
    existingIncentive: ManualIncentiveEditEntry,
    incomingIncentives: ManualIncentiveEditEntry[],
    matchedIncomingIndexes: Set<number>,
    requireSameProvenance: boolean
): { match: ManualIncentiveEditEntry | undefined; matchIndex: number } => {
    // Priority 1: match by _editId (stable row identity)
    if (existingIncentive._editId) {
        const idx = incomingIncentives.findIndex(
            (inc, i) => !matchedIncomingIndexes.has(i) && inc._editId === existingIncentive._editId
        );
        if (idx !== -1) {
            return { match: incomingIncentives[idx], matchIndex: idx };
        }
    }

    if (isEditIdAuthoritative(existingIncentive, incomingIncentives, requireSameProvenance)) {
        return { match: undefined, matchIndex: -1 };
    }

    // Priority 2: match by stable code identity (incoming rows often lack an _editId)
    const idx = incomingIncentives.findIndex(
        (inc, i) => !matchedIncomingIndexes.has(i) && isSameRowAcrossSync(existingIncentive, inc, requireSameProvenance)
    );
    if (idx !== -1) {
        return { match: incomingIncentives[idx], matchIndex: idx };
    }

    return { match: undefined, matchIndex: -1 };
};

/**
 * Processes existing incentives against updated data from Redux
 */
const processExistingIncentives = (
    existingIncentives: ManualIncentiveEditEntry[],
    incomingIncentives: ManualIncentiveEditEntry[],
    requireSameProvenance: boolean
): { retainedIncentives: ManualIncentiveEditEntry[]; matchedIncomingIndexes: Set<number> } => {
    const retainedIncentives: ManualIncentiveEditEntry[] = [];
    const matchedIncomingIndexes = new Set<number>();

    existingIncentives.forEach((existingIncentive) => {
        const { match, matchIndex } = findMatchingIncoming(
            existingIncentive,
            incomingIncentives,
            matchedIncomingIndexes,
            requireSameProvenance
        );

        if (match) {
            retainedIncentives.push(mergeWithUpdatedIncentive(existingIncentive, match));
            matchedIncomingIndexes.add(matchIndex);
        } else if (shouldPreserveIncentive(existingIncentive)) {
            // Keep deleted or newly added incentives
            retainedIncentives.push(existingIncentive);
        } else if (!requireSameProvenance) {
            // Missing from Redux - mark as deleted and keep
            retainedIncentives.push({ ...existingIncentive, deleted: true });
        }
    });

    return { retainedIncentives, matchedIncomingIndexes };
};

const isManualRebateWithMoney = (incentive: ManualIncentiveEditEntry): boolean =>
    incentive.manual === true && Number(incentive.amount) > 0;

const isIdentifiable = (incentive: ManualIncentiveEditEntry, keepUnnamedManualRebate: boolean): boolean =>
    hasCode(incentive) || Boolean(incentive.name) || (keepUnnamedManualRebate && isManualRebateWithMoney(incentive));

/**
 * Adds new incentives that weren't matched to any existing row
 */
const addNewIncentives = (
    incomingIncentives: ManualIncentiveEditEntry[],
    matchedIncomingIndexes: Set<number>,
    retainedIncentives: ManualIncentiveEditEntry[],
    skipUnidentifiable: boolean,
    keepUnnamedManualRebate: boolean
): ManualIncentiveEditEntry[] => {
    const result = [...retainedIncentives];

    incomingIncentives.forEach((incomingIncentive, index) => {
        if (matchedIncomingIndexes.has(index)) {
            return;
        }
        if (skipUnidentifiable && !isIdentifiable(incomingIncentive, keepUnnamedManualRebate)) {
            return;
        }
        result.push({ ...incomingIncentive, _editId: incomingIncentive._editId || generateEditId() });
    });

    return result;
};

const describesSameShownRow = (storedAsShown: ManualIncentiveEditEntry, shown: ManualIncentiveEditEntry): boolean =>
    Boolean(shown._editId) &&
    Boolean(storedAsShown.manual) === Boolean(shown.manual) &&
    String(storedAsShown.code ?? '') === String(shown.code ?? '') &&
    (storedAsShown.name ?? '') === (shown.name ?? '') &&
    storedAsShown.type === shown.type &&
    Number(storedAsShown.amount) === Number(shown.amount);

const takeShownEditId = (
    stored: ManualIncentiveEditEntry,
    shownRows: ManualIncentiveEditEntry[],
    claimedShownIndexes: Set<number>
): string | undefined => {
    const storedAsShown = toShownManualRow(stored);
    const index = shownRows.findIndex((shown, i) => !claimedShownIndexes.has(i) && describesSameShownRow(storedAsShown, shown));
    if (index === -1) {
        return undefined;
    }
    claimedShownIndexes.add(index);
    return shownRows[index]._editId;
};

const claimShownRowsWithStoredIds = (stored: ManualIncentiveEditEntry[], shownRows: ManualIncentiveEditEntry[]): Set<number> => {
    const storedIds = new Set(stored.map((row) => row._editId).filter(Boolean));
    const claimed = new Set<number>();
    shownRows.forEach((shown, index) => {
        if (shown._editId && storedIds.has(shown._editId)) {
            claimed.add(index);
        }
    });
    return claimed;
};

export const stampMissingEditIds = (
    stored: ManualIncentiveEditEntry[],
    shownRows: ManualIncentiveEditEntry[],
    reuseShownEditIds: boolean
): ManualIncentiveEditEntry[] => {
    const claimedShownIndexes = claimShownRowsWithStoredIds(stored, shownRows);
    return stored.map((row) => {
        if (row._editId) {
            return row;
        }
        const reused = reuseShownEditIds ? takeShownEditId(row, shownRows, claimedShownIndexes) : undefined;
        return { ...row, _editId: reused ?? generateEditId() };
    });
};

/**
 * Merges existing incentives with updated incentives from Redux,
 * preserving user edits and deleted/new incentive states.
 * Matches by _editId first (stable row identity), then by code identity.
 */
export const mergeIncentives = (
    existingIncentives: ManualIncentiveEditEntry[],
    incomingIncentives: ManualIncentiveEditEntry[],
    isRestorationEnabled = false,
    keepUnnamedManualRebate = false
): ManualIncentiveEditEntry[] => {
    const { retainedIncentives, matchedIncomingIndexes } = processExistingIncentives(
        existingIncentives,
        incomingIncentives,
        isRestorationEnabled
    );
    return addNewIncentives(
        incomingIncentives,
        matchedIncomingIndexes,
        retainedIncentives,
        isRestorationEnabled,
        keepUnnamedManualRebate
    );
};
